import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Typography,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { Locale } from '../../../services/workout.service';
import { FoodOptionsResponseDto } from '../../../services/admin-food.service';
import { fileService } from '../../../services/file.service';
import { getImageUrl } from '../../../utils/image-url.util';

interface FoodDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  initialData?: any;
  options: FoodOptionsResponseDto;
  isLoading: boolean;
  title: string;
  submitLabel: string;
}

const locales: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

export const FoodDialog: React.FC<FoodDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  options,
  isLoading,
  title,
  submitLabel,
}) => {
  const [nameI18n, setNameI18n] = useState<Partial<Record<Locale, string>>>({});
  const [detailsI18n, setDetailsI18n] = useState<Partial<Record<Locale, string>>>({});
  const [category, setCategory] = useState<string>('');
  const [measurementUnit, setMeasurementUnit] = useState<string>('');
  
  // Macros
  const [caloriesPer100g, setCaloriesPer100g] = useState<number | ''>('');
  const [proteinPer100g, setProteinPer100g] = useState<number | ''>('');
  const [fatPer100g, setFatPer100g] = useState<number | ''>('');
  const [carbsPer100g, setCarbsPer100g] = useState<number | ''>('');
  const [fiberPer100g, setFiberPer100g] = useState<number | ''>('');

  // Additional
  const [gramsPerUnit, setGramsPerUnit] = useState<number | ''>('');
  const [defaultMeasurementValue, setDefaultMeasurementValue] = useState<number | ''>('');
  const [externalLink, setExternalLink] = useState<string>('');

  // Image
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageId, setImageId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Tabs for description
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setNameI18n(initialData.nameI18n || {});
        setDetailsI18n(initialData.detailsI18n || {});
        setCategory(initialData.category || '');
        setMeasurementUnit(initialData.rawMeasurementUnit || '');
        
        setCaloriesPer100g(initialData.caloriesPer100g ?? '');
        setProteinPer100g(initialData.proteinPer100g ?? '');
        setFatPer100g(initialData.fatPer100g ?? '');
        setCarbsPer100g(initialData.carbsPer100g ?? '');
        setFiberPer100g(initialData.fiberPer100g ?? '');
        
        setGramsPerUnit(initialData.gramsPerUnit ?? '');
        setDefaultMeasurementValue(initialData.defaultMeasurementValue ?? '');
        setExternalLink(initialData.externalLink || '');

        // Image
        setImageId(initialData.imageId || null);
        setPreviewUrl(initialData.imageUrl ? getImageUrl(initialData.imageUrl) : null);
      } else {
        // Reset for create mode
        const initialNames: Partial<Record<Locale, string>> = {};
        const initialDetails: Partial<Record<Locale, string>> = {};
        locales.forEach(loc => { 
          initialNames[loc] = ''; 
          initialDetails[loc] = '';
        });
        setNameI18n(initialNames);
        setDetailsI18n(initialDetails);
        setCategory(options.categories.length > 0 ? options.categories[0].value : '');
        setMeasurementUnit(options.measurementUnits.length > 0 ? options.measurementUnits[0].value : '');
        
        setCaloriesPer100g('');
        setProteinPer100g('');
        setFatPer100g('');
        setCarbsPer100g('');
        setFiberPer100g('');
        
        setGramsPerUnit('');
        setDefaultMeasurementValue('');
        setExternalLink('');

        setImageId(null);
        setPreviewUrl(null);
      }
      setSelectedFile(null);
      setActiveTab(0);
    }
  }, [open, initialData, options]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    let finalImageId = imageId;

    if (selectedFile) {
      setIsUploading(true);
      try {
        const uploadedFile = await fileService.uploadFile(selectedFile, 'Food image');
        finalImageId = uploadedFile.id;
      } catch (error) {
        console.error('Failed to upload image:', error);
        setIsUploading(false);
        return; 
      }
      setIsUploading(false);
    }

    onSubmit({
      nameI18n,
      detailsI18n,
      category,
      measurementUnit: measurementUnit || null,
      caloriesPer100g: caloriesPer100g === '' ? null : Number(caloriesPer100g),
      proteinPer100g: proteinPer100g === '' ? null : Number(proteinPer100g),
      fatPer100g: fatPer100g === '' ? null : Number(fatPer100g),
      carbsPer100g: carbsPer100g === '' ? null : Number(carbsPer100g),
      fiberPer100g: fiberPer100g === '' ? null : Number(fiberPer100g),
      gramsPerUnit: gramsPerUnit === '' ? null : Number(gramsPerUnit),
      defaultMeasurementValue: defaultMeasurementValue === '' ? null : Number(defaultMeasurementValue),
      externalLink: externalLink || null,
      imageId: finalImageId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            {/* Left Column: Translations & Image */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Зображення</Typography>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="food-image-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="food-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Завантажити фото
                  </Button>
                </label>
                
                {previewUrl && (
                  <Box sx={{ 
                    border: '1px dashed #ccc', 
                    borderRadius: 1, 
                    p: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5',
                    position: 'relative'
                  }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }}
                    />
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => {
                        setPreviewUrl(null);
                        setSelectedFile(null);
                        setImageId(null);
                      }}
                      sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      Видалити
                    </Button>
                  </Box>
                )}
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Переклади назви</Typography>
              {locales.map(lang => (
                <TextField
                  key={lang}
                  margin="dense"
                  label={`Назва (${lang.toUpperCase()})`}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nameI18n[lang] || ''}
                  onChange={e => setNameI18n({ ...nameI18n, [lang]: e.target.value })}
                  required={lang === 'uk'}
                  size="small"
                />
              ))}
            </Grid>

            {/* Right Column: Properties */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Основні властивості</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Категорія</InputLabel>
                    <Select
                      value={category}
                      label="Категорія"
                      onChange={e => setCategory(e.target.value)}
                    >
                      {options.categories.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Одиниця виміру</InputLabel>
                    <Select
                      value={measurementUnit}
                      label="Одиниця виміру"
                      onChange={e => setMeasurementUnit(e.target.value)}
                    >
                      <MenuItem value=""><em>— не вказано —</em></MenuItem>
                      {options.measurementUnits.map((unit: { value: string; label: string }) => (
                        <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    label="Вага 1 шт (г)"
                    type="number"
                    fullWidth
                    size="small"
                    value={gramsPerUnit}
                    onChange={e => setGramsPerUnit(e.target.value === '' ? '' : Number(e.target.value))}
                    helperText="Якщо шт/порція"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Дефолтна порція"
                    type="number"
                    fullWidth
                    size="small"
                    value={defaultMeasurementValue}
                    onChange={e => setDefaultMeasurementValue(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Зовнішнє посилання"
                    type="url"
                    fullWidth
                    size="small"
                    value={externalLink}
                    onChange={e => setExternalLink(e.target.value)}
                    placeholder="https://..."
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Макронутрієнти (на 100г)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Калорії (ккал)"
                    type="number"
                    fullWidth
                    size="small"
                    value={caloriesPer100g}
                    onChange={e => setCaloriesPer100g(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Білки (г)"
                    type="number"
                    fullWidth
                    size="small"
                    value={proteinPer100g}
                    onChange={e => setProteinPer100g(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Жири (г)"
                    type="number"
                    fullWidth
                    size="small"
                    value={fatPer100g}
                    onChange={e => setFatPer100g(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Вуглеводи (г)"
                    type="number"
                    fullWidth
                    size="small"
                    value={carbsPer100g}
                    onChange={e => setCarbsPer100g(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Клітковина (г)"
                    type="number"
                    fullWidth
                    size="small"
                    value={fiberPer100g}
                    onChange={e => setFiberPer100g(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Description Section */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Опис / Рецепт</Typography>
              <Tabs 
                value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                {locales.map((lang) => (
                  <Tab key={lang} label={lang.toUpperCase()} />
                ))}
              </Tabs>
              {locales.map((lang, index) => (
                <div key={lang} role="tabpanel" hidden={activeTab !== index}>
                  {activeTab === index && (
                    <TextField
                      label={`Опис (${lang.toUpperCase()})`}
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      value={detailsI18n[lang] || ''}
                      onChange={e => setDetailsI18n({ ...detailsI18n, [lang]: e.target.value })}
                      placeholder="Введіть детальний опис, рецепт або інструкцію..."
                    />
                  )}
                </div>
              ))}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button onClick={handleSubmit} disabled={isLoading || isUploading} variant="contained">
          {isLoading || isUploading ? <CircularProgress size={24} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
